/**
 * Los textos de la ayuda contextual, en un solo lugar.
 *
 * POR QUÉ VIVEN AQUÍ Y NO EN CADA PANTALLA: la misma pregunta —«¿qué es un
 * bloqueo?»— aparece en el catálogo, en la disponibilidad y en el mostrador. Si
 * cada pantalla redacta su propia versión, terminan contradiciéndose y nadie se
 * entera. Con un solo archivo, corregir una explicación la corrige en todas.
 *
 * LÍMITE DURO DE ~150 CARACTERES POR TEXTO, a propósito. Un globo de ayuda que
 * no cabe de un vistazo no se lee: se cierra. Si una explicación no entra en dos
 * renglones, el problema no es el globo — es que la pantalla no se explica sola
 * y hay que arreglar la pantalla, no alargar el texto.
 *
 * NO SE INVENTA NADA DE LA INSTITUCIÓN (regla 2 del proyecto). Aquí no hay
 * horarios, ni número de alumnos, ni cuántas reservaciones se permiten: todo eso
 * es dato configurable que carga la escuela. Estos textos explican QUÉ significa
 * cada concepto del sistema, nunca CUÁNTO vale en esta escuela.
 */

export interface ContenidoDeAyuda {
  titulo: string
  texto: string
}

/**
 * `satisfies` y no una anotación de tipo: así el objeto cumple el contrato de
 * `Record<string, ContenidoDeAyuda>` y, al mismo tiempo, TypeScript conserva las
 * claves literales para `ClaveDeAyuda`. Con una anotación plana, `<Ayuda
 * clave="tipografo" />` compilaría y reventaría en pantalla.
 */
export const AYUDA = {
  reservar: {
    titulo: 'Reservar un espacio',
    texto:
      'Eliges día, espacio y un bloque libre. La reservación queda a tu nombre y aparece en «Mis reservaciones», que es donde también se cancela.',
  },

  'limite-simultaneas': {
    titulo: 'Reservaciones a la vez',
    texto:
      'Cuántas reservaciones activas puedes tener al mismo tiempo. Lo fija tu rol; en blanco significa sin límite, no cero.',
  },

  bloqueo: {
    titulo: 'Qué es un bloqueo',
    texto:
      'Un rango de horas en que un espacio no se puede reservar. Lo declara quien administra, siempre con un motivo, y queda en la auditoría.',
  },

  'dia-no-habil': {
    titulo: 'Qué es un día no hábil',
    texto:
      'Una fecha declarada sin servicio. Ese día no hay bloques que reservar, aunque el horario normal de la escuela diga otra cosa.',
  },

  rol: {
    titulo: 'Qué es un rol',
    texto:
      'Un conjunto de permisos con nombre. Se crea y se edita desde administración, sin tocar el código ni volver a desplegar el sistema.',
  },

  permiso: {
    titulo: 'Qué es un permiso',
    texto:
      'La unidad de lo que alguien puede hacer. El sistema siempre pregunta por permisos, nunca por el nombre del rol que los tiene.',
  },

  tutoria: {
    titulo: 'Qué es una tutoría',
    texto:
      'El vínculo entre un alumno y quien lo acompaña. Para acompañar, el rol de esa persona necesita el permiso «acompanar_alumnos».',
  },

  'responsable-de-espacio': {
    titulo: 'Responsable de un espacio',
    texto:
      'Quien responde por un espacio concreto. Su rol necesita el permiso «responsable_de_espacio», y la asignación se hace desde administración.',
  },

  'horario-de-operacion': {
    titulo: 'Por qué hace falta el horario',
    texto:
      'Sin horario cargado el sistema cierra todos los días a propósito, para no ofrecer horas que quizá no existan. Capturarlo es lo que enciende todo.',
  },

  'aviso-sin-envio': {
    titulo: 'Qué es «sin envío aún»',
    texto:
      'El aviso se generó y quedó esperando. Ese canal todavía no tiene envío configurado, así que por ahí no sale nada del sistema todavía.',
  },
} satisfies Record<string, ContenidoDeAyuda>

/** Las claves que existen de verdad. Pedir otra no compila. */
export type ClaveDeAyuda = keyof typeof AYUDA
